import { useCallback, useMemo } from 'react';

/**
 * Custom hook that provides responsive grid layout configuration and utilities.
 * Calculates optimal column counts based on container width and provides grid configuration functions.
 *
 * @param {number} containerWidth - The width of the container in pixels
 * @returns {{
 *   grid: {gutter: number, xs: number, sm: number, md: number, lg: number, xl: number, xxl: number},
 *   columnsCount: number,
 *   createGridConfig: Function
 * }} Object containing grid configuration, current column count, and grid config creator function
 */
export default function useGridLayout(containerWidth) {
  const grid = useMemo(
    () => ({
      gutter: 32,
      xs: 1,
      sm: 1,
      md: 3,
      lg: 5,
      xl: 5,
      xxl: 5,
    }),
    [],
  );

  // Grid configuration based on screen size
  const getColumnsCount = useCallback(() => {
    if (!containerWidth) return 5;
    if (containerWidth < 576) return 1; // xs
    if (containerWidth < 768) return 1; // sm
    if (containerWidth < 992) return 3; // md
    return 5; // lg, xl, xxl
  }, [containerWidth]);

  const columnsCount = getColumnsCount();

  /**
   * Creates grid configuration object for virtualized grid layouts
   *
   * @param {boolean} isGridLayout - Whether grid layout is enabled
   * @param {boolean} virtual - Whether virtual scrolling is enabled
   * @param {Array} data - Array of data items to display
   * @param {Function} getItemHeight - Function to get height of individual items
   * @returns {{gutter: number, itemWidth: number, itemHeight: number, totalGridHeight: number}|null} Grid configuration or null if not applicable
   */
  const createGridConfig = useCallback(
    (isGridLayout, virtual, data, getItemHeight) => {
      if (!isGridLayout || !virtual) return null;

      const gutter = 32;
      const itemWidth = (containerWidth - gutter * (columnsCount + 1)) / columnsCount;
      const itemHeight = getItemHeight(0);
      const totalGridHeight = Math.ceil(data.length / columnsCount) * (itemHeight + gutter);

      return {
        gutter,
        itemWidth,
        itemHeight,
        totalGridHeight,
      };
    },
    [containerWidth, columnsCount],
  );

  return {
    grid,
    columnsCount,
    createGridConfig,
  };
}
